import type { Flatfile } from '@flatfile/api-internal'
import { AppType } from '@flatfile/api-internal/api'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  Button,
  cn,
  IconName,
  Text,
  useToast,
} from '@flatfile/baseline'
import { FC, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'

import { useCollection } from '../../resources/hooks/useCollection'
import { useCreateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import { appConfigMap, getInstalledApp } from '../../utils/appsUtils'
import { CustomAppLink } from './CustomAppLink'
import { globalLinks, resources, support } from './links'
import { SideBarLink } from './SideBarLink'
import { UserDropdownMenu } from './UserDropdownMenu'

import { FlatfileLogo } from '../../assets/flatfile-logo'
import { useEntitlements } from '../../hooks/useEntitlements'
import UpdateAppModal from '../../pages/CustomApps/CustomAppModal'
import { demos } from '../../pages/CustomApps/CustomAppsHello.page'
import useResourceList from '../../resources/hooks/useResourceList'
import { usePylon } from '../../support/PylonContext'
import { useTracking } from '../../support/useTracking'
import CreateAppPath from '../Apps/CreateAppModalPath'
import { renderMappedIcon } from '../Shared/IconMapper'
import { AppSwitcher } from './AppSwitcher'
import { EnvironmentSwitcher } from './EnvironmentSwitcher'

export const SideBar: FC = () => {
  const location = useLocation()
  const nav = useNavigate()
  const { id = '' } = useParams()

  const { showNewMessage } = usePylon()
  const { identity, hasPermission } = useIdentity()
  const { toast } = useToast()

  const apps = useCollection('APPS')

  const [createApp] = useCreateResource('APPS')

  const { environment, hasEnvAccess } = useActiveEnvironment()

  const tracker = useTracking()
  const { hasEntitlement } = useEntitlements()
  const projectsFlag = hasEntitlement('projectsEnabled')

  // Alert state for showing error messages
  const [alertInfo, setAlertInfo] = useState<{ show: boolean; title: string; description: string }>({
    show: false,
    title: '',
    description: '',
  })

  // Hide alert after timeout
  useEffect(() => {
    if (alertInfo.show) {
      const timer = setTimeout(() => {
        setAlertInfo({ show: false, title: '', description: '' })
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [alertInfo])

  useEffect(() => {
    const intent = identity.user.metadata?.productIntent
    if (!apps.length && intent) {
      switch (intent) {
        case 'portal':
          createPortalApp()
          break
        case 'workbooks':
          nav('/apps/workbooks/start/')
          break
        case 'mapping':
          createMappingApp()
          break
        case 'projects':
          projectsFlag ? createProjectsApp() : nav('/apps/projects/learn')
      }
    }
  }, [apps, identity.user])

  const createPortalApp = useCallback(() => {
    createApp(appConfigMap[AppType.Portal])
      .then((app) => {
        tracker.capture('app / portal / init', {
          appId: app.id,
        })
        getCollection('APPS').refresh()
        nav('/apps/portal/start/')
      })
      .catch(() => {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to install Portal app',
        })
      })
  }, [createApp, nav, tracker, toast])

  const createMappingApp = useCallback(() => {
    createApp(appConfigMap[AppType.Mapping])
      .then((app) => {
        tracker.capture('app / mapping / init', {
          appId: app.id,
        })
        getCollection('APPS').refresh()
        nav('/apps/mapping/start/')
      })
      .catch(() => {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to install Mapping app',
        })
      })
  }, [createApp, nav, tracker, toast])

  const createProjectsApp = useCallback(() => {
    createApp(appConfigMap[AppType.Projects])
      .then((app) => {
        tracker.capture('app / projects / init', {
          appId: app.id,
        })
        getCollection('APPS').refresh()
        nav('/apps/projects/start/')
      })
      .catch(() => {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to install Projects app',
        })
      })
  }, [createApp, nav, tracker, toast])

  const [currentlyOpenApp, setCurrentlyOpenApp] = useState<string>(useParams().id || '')
  const [showAllApps, setShowAllApps] = useState<boolean>(false)
  const noCustomAppsOpen = !location.pathname.includes('/apps/custom')

  const customApps = useMemo(() => {
    const mainApps = ['portal', 'projects', 'workbooks', 'mapping']

    const excludedNamespaces = [...mainApps, ...demos.map((demo) => demo.namespace)]
    return apps.filter(
      (app: Flatfile.App) => app.type === AppType.Custom && !excludedNamespaces.includes(app.namespace)
    )
  }, [apps])

  const activeDemoApps = useMemo(() => {
    return apps.filter(
      (app: Flatfile.App) => app.type === AppType.Custom && demos.map((demo) => demo.namespace).includes(app.namespace)
    )
  }, [apps])
  const setOpenApp = useCallback(
    (appId: string) => {
      setCurrentlyOpenApp(appId)
    },
    [currentlyOpenApp]
  )

  // Helper function to determine the action based on the app's state
  function determineAction(installedApp: Flatfile.App | undefined) {
    if (!installedApp) return 'Install'
    if (!installedApp.activatedAt) return 'Finish Setup'
  }

  // Helper function to determine the environment filters based on the app's state
  function determineEnvironmentFilters(installedApp: Flatfile.App | undefined) {
    // Assuming default behavior for apps without explicitly set environmentFilters
    const defaultFilters = { type: ['DEV'] } // Default to 'DEV' if not specified
    return installedApp?.environmentFilters || defaultFilters
  }

  // Generalized onClick handler to reduce repetition
  async function handleClick(appType: AppType, createAppFunc: () => void, startPath: any) {
    const app = getInstalledApp(apps, appType)
    if (!app) {
      createAppFunc()
    } else if (!app.activatedAt) {
      nav(`/apps/${startPath}/start/`)
    } else {
      nav(`/apps/${app.namespace}/list/recent/`)
    }
  }

  const defaultEnvironmentFilters = { type: ['DEV'] }

  // Use a function to generate the objects to avoid repetition
  function generateAppObject(
    icon: string,
    label: string,
    appType: AppType,
    createAppFunc: () => void,
    startPath: string,
    systemIcon: boolean,
    _environmentFilters: { type: string[] }
  ) {
    const installedApp = getInstalledApp(apps, appType)
    // Use environmentFilters directly or fall back to default
    const effectiveEnvironmentFilters = determineEnvironmentFilters(installedApp) || defaultEnvironmentFilters

    return {
      icon,
      label,
      action: determineAction(installedApp),
      onClick: () => handleClick(appType, createAppFunc, startPath),
      systemIcon,
      environmentFilters: effectiveEnvironmentFilters || _environmentFilters, // Include environment filters here
    }
  }

  function generateProjectsObject() {
    const environmentFilters = { type: ['DEV'] }

    if (projectsFlag) {
      return generateAppObject(
        'bag-2',
        'Projects',
        AppType.Projects,
        createProjectsApp,
        'projects',
        true,
        environmentFilters
      )
    }
    const projectsApp = getInstalledApp(apps, AppType.Projects)
    return {
      icon: 'bag-2',
      label: 'Projects',
      action: projectsApp ? undefined : 'Request',
      link: `/apps/${projectsApp?.namespace ?? 'projects/learn'}`,
      notifications: 4, // Assuming static, adjust if dynamic
      systemIcon: true,
      environmentFilters, // Correct placement of the environmentFilters property
    }
  }

  const platformApps = useMemo(() => {
    if (!hasEnvAccess) {
      return [
        {
          icon: 'bag-2',
          label: 'Sessions',
          action: undefined,
          onClick: async () => {
            nav(`/`)
          },
          systemIcon: true,
          environmentFilters: defaultEnvironmentFilters,
        },
      ]
    }

    return [
      generateAppObject(
        'cmd-box',
        'Portal',
        AppType.Portal,
        createPortalApp,
        'portal',
        true,
        defaultEnvironmentFilters
      ),
      generateProjectsObject(), // Ensure this function also returns the environmentFilters correctly
      {
        icon: 'table',
        label: 'Workbooks',
        action: getInstalledApp(apps, AppType.Workbooks) ? undefined : 'Learn More',
        link: `/apps/${getInstalledApp(apps, AppType.Workbooks)?.namespace ?? 'workbooks/start'}`,
        systemIcon: true,
        environmentFilters: defaultEnvironmentFilters,
      },
      generateAppObject(
        'map-pin-flat',
        'Mapping',
        AppType.Mapping,
        createMappingApp,
        'mapping',
        true,
        defaultEnvironmentFilters
      ),
    ]
  }, [apps, environment]) // Make sure environment is included in the dependency array if its state could affect the output

  // todo make this actual logic
  const portalCurrentlyActive = location.pathname.includes('/apps/portal')
  const mappingCurrentlyActive = location.pathname.includes('/apps/mapping')
  const projectsCurrentlyActive = location.pathname.includes('/apps/projects') && projectsFlag

  const defaultApps = useResourceList('APPS').response.data.filter(
    (app: Flatfile.App) =>
      app.namespace === 'portal' ||
      app.namespace === 'mapping' ||
      app.namespace === 'projects' ||
      app.namespace === 'workbooks'
  )

  const portalApp = defaultApps[defaultApps.findIndex((app) => app.namespace === 'portal')] || null
  const mappingApp = defaultApps[defaultApps.findIndex((app) => app.namespace === 'mapping')] || null
  const projectsApp = defaultApps[defaultApps.findIndex((app) => app.namespace === 'projects')] || null
  // const workbooksApp = defaultApps[defaultApps.findIndex((app) => app.namespace === 'workbooks')] || null

  const filteredApps = apps.filter((app) =>
    environment?.isProd
      ? app.environmentFilters.type?.includes('PROD') ?? false
      : app.environmentFilters.type?.includes('DEV') ?? false
  )

  performance.mark('fmp')
  return (
    <nav className='h-full flex flex-col bg-surface-base w-[275px] animate-fadeIn border-r border-table-border'>
      {alertInfo.show && (
        <div className='absolute top-4 right-4 z-50 w-80'>
          <Alert variant='destructive'>
            <AlertTitle>{alertInfo.title}</AlertTitle>
            <AlertDescription>{alertInfo.description}</AlertDescription>
          </Alert>
        </div>
      )}
      <div className='flex justify-between items-center gap-2 px-4 py-4'>
        <div className='flex items-center gap-0'>
          <FlatfileLogo
            style={{ cursor: 'pointer' }}
            onClick={() => {
              nav('/home')
            }}
          />
        </div>
        {hasEnvAccess && <EnvironmentSwitcher />}
      </div>
      <div className='flex-1 overflow-y-auto'>
        <div className='px-4 pb-4'>
          <div className=''>
            <NavLink to='/home' relative='path'>
              <Button
                variant='ghost'
                className='align-bottom justify-start p-1.5 -ml-1.5 w-[calc(100%+0.75rem)] hover:bg-interactive-state-selected-enabled'
                icon='home-line'
                iconProps={{ size: 16, className: 'text-primary' }}
              >
                <Text variant='body' emphasized className='text-primary'>
                  Apps
                </Text>
              </Button>
            </NavLink>
          </div>
          <div className='py-3' hidden={!portalCurrentlyActive}>
            <div className='flex justify-between items-baseline w-full'>
              <AppSwitcher
                platformApps={platformApps}
                customApps={customApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                demoApps={activeDemoApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                name='Portal'
                icon='cmd-box'
              />
              {hasPermission({ resource: identity.account, permissionKey: 'app:update' }) && (
                <UpdateAppModal currentApp={portalApp} isBuiltInApp />
              )}
            </div>
            <div>
              {!portalApp ||
                (!portalApp.activatedAt && (
                  <NavLink to='/apps/portal/start' relative='path'>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                        location.pathname === '/apps/portal/start/' && 'text-primary font-medium '
                      )}
                      icon='numbered-list'
                      iconProps={{ className: 'text-primary' }}
                    >
                      <Text variant='body' className='text-primary'>
                        Setup Guide
                      </Text>
                    </Button>
                  </NavLink>
                ))}

              <NavLink to='/apps/portal/usage' relative='path'>
                <Button
                  variant='ghost'
                  className={cn(
                    'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                    location.pathname === '/apps/portal/usage' && 'text-primary font-medium '
                  )}
                  icon='analytics'
                  iconProps={{ className: 'text-primary' }}
                >
                  <Text variant='body' className='text-primary'>
                    Usage
                  </Text>
                </Button>
              </NavLink>

              <NavLink to='/apps/portal/list/recent/' relative='path'>
                <Button
                  variant='ghost'
                  className={cn(
                    'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                    location.pathname === '/apps/portal/list/recent/' && 'text-primary font-medium '
                  )}
                  icon='clipboard-2'
                  iconProps={{ className: 'text-primary' }}
                >
                  <Text variant='body' className='text-primary'>
                    Recent Sessions
                  </Text>
                </Button>
              </NavLink>
            </div>
          </div>
          <div className='py-3' hidden={!mappingCurrentlyActive}>
            <div className='flex justify-between items-baseline w-full'>
              <AppSwitcher
                platformApps={platformApps}
                customApps={customApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                demoApps={activeDemoApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                name='Mapping'
                icon='map-pin-flat'
              />
              {hasPermission({ resource: identity.account, permissionKey: 'app:update' }) && (
                <UpdateAppModal currentApp={mappingApp} isBuiltInApp />
              )}
            </div>

            <div>
              {!mappingApp ||
                (!mappingApp.activatedAt && (
                  <NavLink to='/apps/mapping/start/' relative='path'>
                    <Button
                      variant='ghost'
                      className={cn(
                        'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                        location.pathname === '/apps/mapping/start/' && 'text-primary font-medium '
                      )}
                      icon='numbered-list'
                      iconProps={{ className: 'text-primary' }}
                    >
                      <Text variant='body' className='text-primary'>
                        Setup Guide
                      </Text>
                    </Button>
                  </NavLink>
                ))}
              <NavLink to='/apps/mapping/list/recent/' relative='path'>
                <Button
                  variant='ghost'
                  className={cn(
                    'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                    location.pathname === '/apps/mapping/list/recent/' && 'text-primary font-medium '
                  )}
                  icon='clipboard-2'
                  iconProps={{ className: 'text-primary' }}
                >
                  <Text variant='body' className='text-primary'>
                    Mapping History
                  </Text>
                </Button>
              </NavLink>
            </div>
          </div>
          {projectsFlag && (
            <div className='py-3' hidden={!projectsCurrentlyActive}>
              <div className='flex justify-between items-baseline w-full'>
                <AppSwitcher
                  platformApps={platformApps}
                  customApps={customApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                  demoApps={activeDemoApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                  name='Projects'
                  icon='bag-2'
                />
                {hasPermission({ resource: identity.account, permissionKey: 'app:update' }) && (
                  <UpdateAppModal currentApp={projectsApp} isBuiltInApp />
                )}
              </div>
              <div>
                {!projectsApp ||
                  (!projectsApp.activatedAt && (
                    <NavLink to='/apps/projects/start/' relative='path'>
                      <Button
                        variant='ghost'
                        className={cn(
                          'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                          location.pathname === '/apps/projects/start/' && 'text-primary font-medium '
                        )}
                        icon='numbered-list'
                        iconProps={{ className: 'text-primary' }}
                      >
                        <Text variant='body' className='text-primary'>
                          Setup Guide
                        </Text>
                      </Button>
                    </NavLink>
                  ))}
                <NavLink to='/apps/projects/list/recent/' relative='path'>
                  <Button
                    variant='ghost'
                    className={cn(
                      'w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled',
                      location.pathname === '/apps/projects/list/recent/' && 'text-primary font-medium '
                    )}
                    icon='clipboard-2'
                    iconProps={{ className: 'text-primary' }}
                  >
                    <Text variant='body' className='text-primary'>
                      Recent Projects
                    </Text>
                  </Button>
                </NavLink>
              </div>
            </div>
          )}
          {[...customApps, ...activeDemoApps].map((app) => {
            return (
              <CustomAppLink
                key={app.id}
                app={app}
                open={app.id === id}
                platformApps={platformApps}
                customApps={customApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
                demoApps={activeDemoApps.map((app) => ({ ...app, activatedAt: app.activatedAt?.toISOString() }))}
              />
            )
          })}
          <div className='py-1' hidden={!noCustomAppsOpen}>
            <div className='py-3 pb-3 flex justify-between items-center'>
              <Text variant='subheadline' emphasized className='tracking-wider uppercase text-muted'>
                APPS
              </Text>
              <Suspense
                fallback={
                  <Button
                    size='icon-sm'
                    variant='outline'
                    className='flex-shrink-0'
                    icon='loader'
                    iconProps={{ size: 12 }}
                  ></Button>
                }
              >
                {hasPermission({ resource: identity.account, permissionKey: 'app:create' }) && <CreateAppPath mini />}
              </Suspense>
            </div>
            {apps.length === 0 ? (
              <Text variant='body' className='text-gray-500 italic'>
                No apps installed.
              </Text>
            ) : filteredApps.length === 0 ? (
              <Text variant='body' className='text-gray-500 italic'>
                No apps have been installed in your {environment?.isProd ? 'production' : 'dev'} environments.
              </Text>
            ) : null}
            <div>
              {(() => {
                const filteredLinks = platformApps
                  .filter((link) => !link.action || link.action === 'Finish Setup')
                  .filter((app) =>
                    environment?.isProd
                      ? app.environmentFilters.type?.includes('PROD') ?? false
                      : app.environmentFilters.type?.includes('DEV') ?? false
                  )

                return (
                  filteredLinks.length > 0 &&
                  filteredLinks.map((link, index) => {
                    if (!showAllApps && index >= 4) return null
                    return <SideBarLink key={link.label} {...link} icon={link.icon as IconName} />
                  })
                )
              })()}

              {(() => {
                const filteredCustomApps = customApps.filter((app) =>
                  environment?.isProd
                    ? app.environmentFilters.type?.includes('PROD') ?? false
                    : app.environmentFilters.type?.includes('DEV') ?? false
                )

                return filteredCustomApps.map((app, index) => {
                  const platformAppsCount = platformApps.filter(
                    (link) =>
                      (!link.action || link.action === 'Finish Setup') &&
                      (environment?.isProd
                        ? link.environmentFilters.type?.includes('PROD') ?? false
                        : link.environmentFilters.type?.includes('DEV') ?? false)
                  ).length

                  const totalIndex = platformAppsCount + index

                  if (!showAllApps && totalIndex >= 4) return null

                  const appCurrentlyOpen = currentlyOpenApp === app.id

                  const appJsx = noCustomAppsOpen ? (
                    <SideBarLink
                      key={app.id}
                      icon={(app.icon as IconName) || 'app'}
                      label={app.name}
                      action={!app.activatedAt ? 'Finish Setup' : undefined}
                      onClick={async () => {
                        setOpenApp(app.id)
                        nav(`/apps/custom/${app.id}/${!app.activatedAt ? 'start' : 'list'}/`)
                      }}
                      renderCustomIcon={(icon) =>
                        renderMappedIcon({
                          iconName: icon,
                          className: 'text-primary',
                        })
                      }
                    />
                  ) : null
                  return noCustomAppsOpen ? appJsx : appCurrentlyOpen ? appJsx : null
                })
              })()}

              {(() => {
                const allVisibleApps = [
                  ...platformApps.filter(
                    (link) =>
                      (!link.action || link.action === 'Finish Setup') &&
                      (environment?.isProd
                        ? link.environmentFilters.type?.includes('PROD') ?? false
                        : link.environmentFilters.type?.includes('DEV') ?? false)
                  ),
                  ...customApps.filter((app) =>
                    environment?.isProd
                      ? app.environmentFilters.type?.includes('PROD') ?? false
                      : app.environmentFilters.type?.includes('DEV') ?? false
                  ),
                ]

                // If we have demos to show, add 1 to account for the Demos accordion
                const hasDemos =
                  activeDemoApps.filter((app) =>
                    environment?.isProd
                      ? app.environmentFilters.type?.includes('PROD') ?? false
                      : app.environmentFilters.type?.includes('DEV') ?? false
                  ).length > 0

                const totalVisibleApps = hasDemos ? [...allVisibleApps, 'demos'] : allVisibleApps

                if (totalVisibleApps.length > 4 && !showAllApps) {
                  return (
                    <Button
                      variant='ghost'
                      className='w-[calc(100%+0.75rem)] justify-end p-1.5 -ml-1.5 mr-0 mt-2 hover:bg-interactive-state-selected-enabled'
                      onClick={() => setShowAllApps(true)}
                      icon='plus-small'
                      size='sm'
                      iconProps={{ className: 'text-primary' }}
                    >
                      <Text variant='body' className='!text-primary text-xxs'>
                        See all
                      </Text>
                    </Button>
                  )
                }
                return null
              })()}
            </div>
            {(() => {
              const filteredDemoApps = activeDemoApps.filter((app) =>
                environment?.isProd
                  ? app.environmentFilters.type?.includes('PROD') ?? false
                  : app.environmentFilters.type?.includes('DEV') ?? false
              )

              if (filteredDemoApps.length === 0) return null

              // Count how many apps are already shown
              const visibleAppCount =
                platformApps.filter(
                  (link) =>
                    (!link.action || link.action === 'Finish Setup') &&
                    (environment?.isProd
                      ? link.environmentFilters.type?.includes('PROD') ?? false
                      : link.environmentFilters.type?.includes('DEV') ?? false)
                ).length +
                customApps.filter((app) =>
                  environment?.isProd
                    ? app.environmentFilters.type?.includes('PROD') ?? false
                    : app.environmentFilters.type?.includes('DEV') ?? false
                ).length

              // Hide demos accordion if we've already shown 4 apps and showAllApps is false
              if (!showAllApps && visibleAppCount >= 4) return null

              return (
                <div className=''>
                  <Accordion type='single' collapsible>
                    <AccordionItem value='demos' className='p-0 border-0'>
                      <AccordionTrigger className='p-0 hover:no-underline'>
                        <Button
                          variant='ghost'
                          className='w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled'
                          icon='folder-paper'
                          iconProps={{ className: 'text-primary' }}
                        >
                          <Text variant='body' className='text-primary'>
                            Demos
                          </Text>
                        </Button>
                      </AccordionTrigger>
                      <AccordionContent>
                        {filteredDemoApps.map((app) => {
                          const IconToRender =
                            (demos.find((d) => d.namespace === app.namespace)?.icon.toString() as IconName) || 'app'

                          return (
                            <SideBarLink
                              key={app.id}
                              icon={IconToRender}
                              label={app.name}
                              action={!app.activatedAt ? 'Finish Setup' : undefined}
                              onClick={async () => {
                                setOpenApp(app.id)
                                nav(`/apps/custom/${app.id}/${!app.activatedAt ? 'start' : 'list'}/`)
                              }}
                              renderCustomIcon={(icon) =>
                                renderMappedIcon({ iconName: icon as IconName, className: 'text-primary' })
                              }
                            />
                          )
                        })}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )
            })()}
            {showAllApps && (
              <Button
                variant='ghost'
                className='w-[calc(100%+0.75rem)] justify-end p-1.5 -ml-1.5 mr-0 mt-2 hover:bg-interactive-state-selected-enabled'
                onClick={() => setShowAllApps(false)}
                icon='minus-small'
                size='sm'
                iconProps={{ className: 'text-primary' }}
              >
                <Text variant='body' className='!text-primary text-xxs'>
                  See less
                </Text>
              </Button>
            )}
          </div>
          <div className='py-1' hidden={!hasEnvAccess}>
            <Accordion type='single' collapsible defaultValue='environment'>
              <AccordionItem value='environment' className='border-0'>
                <AccordionTrigger className='py-3 px-0 hover:no-underline'>
                  <Text variant='subheadline' emphasized className='tracking-wider uppercase text-muted'>
                    ENVIRONMENT
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  {globalLinks
                    .filter((link) => link.entitlement === undefined || hasEntitlement(link.entitlement))
                    .map((link) => {
                      return <SideBarLink className='text-primary' key={link.label} {...link} systemIcon />
                    })}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <div className='py-1'>
            <Accordion type='single' collapsible defaultValue='resources'>
              <AccordionItem value='resources' className='border-0'>
                <AccordionTrigger className='py-3 px-0 hover:no-underline'>
                  <Text variant='subheadline' emphasized className='tracking-wider uppercase text-muted'>
                    RESOURCES
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  {resources.map((collection) => (
                    <Button
                      key={collection.label}
                      variant='ghost'
                      className='w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled'
                      icon={collection.icon || 'app'}
                      iconProps={{ className: 'text-primary' }}
                      onClick={() => {
                        window.open(collection.url, '_blank')
                      }}
                    >
                      <Text variant='body' className='text-primary'>
                        {collection.label}
                      </Text>
                    </Button>
                  ))}
                  {support.map((collection) => (
                    <Button
                      key={collection.label}
                      variant='ghost'
                      className='w-[calc(100%+0.75rem)] justify-start p-1.5 -ml-1.5 mr-0 hover:bg-interactive-state-selected-enabled'
                      icon={collection.icon || 'app'}
                      iconProps={{ className: 'text-primary' }}
                      onClick={() => {
                        showNewMessage()
                      }}
                    >
                      <Text variant='body' className='text-primary'>
                        {collection.label}
                      </Text>
                    </Button>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
      <div className='border-t border-gray-300 py-4 px-4'>
        <div className='flex items-center'>
          <Avatar size='md' className='mr-1'>
            <AvatarFallback>{identity.user.name.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>

          <div className='flex-1 pl-1'>
            <Text variant='callout' emphasized className='truncate max-w-[135px] block text-primary'>
              {identity.user.name}
            </Text>

            <Text variant='footnote' className='truncate max-w-[135px] block !text-muted text-xxs'>
              {identity.user.email}
            </Text>
          </div>
          <UserDropdownMenu>
            <Button
              size='icon-sm'
              variant='outline'
              className='mr-1.5'
              icon='dot-grid-1x3-vertical'
              iconProps={{ size: 16 }}
            ></Button>
          </UserDropdownMenu>
        </div>
      </div>
    </nav>
  )
}
